/**
 * AppAuth0Provider — wraps the whole app in Auth0's session context.
 *
 * Reads its config from Vite env vars (see `.env.local`). Fails CLOSED: if Auth0
 * isn't configured (missing domain/clientId) it throws, so a misconfigured build
 * can never silently serve the app without a login wall.
 */

import type { ReactNode } from 'react'
import { Auth0Provider } from '@auth0/auth0-react'

// Injected at build time by Vite. `string | undefined` because a fresh checkout
// may not have set up `.env.local` yet.
const domain = import.meta.env.VITE_AUTH0_DOMAIN as string | undefined
const clientId = import.meta.env.VITE_AUTH0_CLIENT_ID as string | undefined
// Optional for now — only needed once the backend verifies tokens. When unset,
// `authorizationParams.audience` is just `undefined`, which Auth0 ignores.
const audience = import.meta.env.VITE_AUTH0_AUDIENCE as string | undefined

export function AppAuth0Provider({ children }: { children: ReactNode }) {
  // Fail closed, in every environment: a missing tenant must be a loud crash,
  // never a silently ungated app. (Caught by the app-level ErrorBoundary.)
  if (!domain || !clientId) {
    throw new Error(
      'Auth0 is not configured: set VITE_AUTH0_DOMAIN and VITE_AUTH0_CLIENT_ID in .env.local',
    )
  }

  return (
    <Auth0Provider
      domain={domain}
      clientId={clientId}
      authorizationParams={{
        // Where Auth0 sends the user back after login. Must match an "Allowed
        // Callback URL" in the dashboard. origin = http://localhost:5173 in dev.
        redirect_uri: window.location.origin,
        // Requests an ACCESS TOKEN aimed at our backend API. Harmless when
        // undefined; we'll set it in the backend phase.
        audience,
      }}
      // Persist the session in localStorage + use refresh tokens so a page
      // reload doesn't log the user out. (The default is in-memory, which drops
      // the session on refresh because browsers block the silent-refresh iframe.)
      cacheLocation="localstorage"
      useRefreshTokens
    >
      {children}
    </Auth0Provider>
  )
}
